import { ReactNode } from 'react';

export interface CreateTagRequest extends Omit<Tag, '_id' | 'createdBy' | 'created' | 'category' | 'subcategory'> {
  category: string;
  subcategory?: string | null;
}

export interface UpdateTagRequestBody
  extends Omit<Tag, '_id' | 'createdBy' | 'created' | 'category' | 'subcategory' | 'updated' | 'updateBy'> {
  category: string;
  subcategory?: string | null;
}

export interface UpdateTagRequest {
  tagId: string;
  body: UpdateTagRequestBody;
}

export interface GetTagsRequest {
  label: string | undefined;
  category: string | undefined;
  subcategory: string | undefined;
  populate: boolean | undefined;
  skip?: number;
  limit?: 10 | 20 | 100;
}

export interface GetTagsResponse {
  tags: Tag[];
  count: number;
}

export interface Tag {
  _id: string;
  label: string;
  colour: string;
  weight: number;
  category: string;
  subcategory?: string | null;
  created: Date;
  createdBy: string;
  updated?: Date;
  updateBy?: string;
}

export interface TagProps {
  tag: Tag;
  tagCategories: TagCategory[];
  handleDeleteTag: () => void;
  handleUpdateTag: (updateTagPayload: UpdateTagRequest) => void;
}
export interface TagsProps {
  handleCreateTag: (newTag: CreateTagRequest) => void;
  handleDeleteTag: (tagId: string) => void;
  handleUpdateTag: (updateRequest: UpdateTagRequest) => void;
  tags: Tag[];
  createdTag?: Tag;
  resetCreateTag: () => void;
  tagCategories: TagCategory[];
  isCreating: boolean;
  selectedCategoryId?: string;
  selectedSubcategoryId?: string;
  setSelectedCategoryId: (categoryId: string) => void;
  setSelectedSubcategoryId: (subcategoryId: string) => void;
  totalPages: number;
  onPageChange: (newPage: number) => void;
  currentPage: number;
  onLabelChange: (newLabel: string) => void;
}

export interface UpdateTagCategoryRequest {
  tagCategoryId: string;
  body: Partial<Pick<TagCategory, 'scopes' | 'adminOnlyTagCreation'>>;
}

export interface TagCategoryProps {
  createTagCategory: (newTagCategory: Omit<TagCategory, '_id' | 'createdBy'>) => void;
  deleteTagCategory: (tagCategoryId: string) => void;
  updateTagCategory: (updateRequest: UpdateTagCategoryRequest) => void;
  tagCategory: TagCategory;
  setAddTagCategoryModalOpen: (open: boolean) => void;
  setSelectedParentCategoryId: (parentCategoryId: string) => void;
}

export interface TagCategoriesProps {
  createTagCategory: (newTagCategory: Omit<TagCategory, '_id' | 'createdBy'>) => void;
  deleteTagCategory: (tagCategoryId: string) => void;
  updateTagCategory: (updateRequest: UpdateTagCategoryRequest) => void;
  tagCategories: TagCategory[];
  isCreating: boolean;
  createdTagCategory?: TagCategory;
  resetCreateTagCategory: () => void;
}

export interface TagCategory {
  _id: string;
  label: string;
  adminOnlyTagCreation: boolean;
  scopes: string[];
  parentCategory?: string;
  subcategories: TagCategory[];
  created: Date;
  createdBy: string;
  updated?: Date;
  updateBy?: string;
}

export interface ModalProps {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  triggerElement?: ReactNode;
  isCreating: boolean;
}

export interface AddTagCategoryModalProps extends ModalProps {
  handleCreateTagCategory: (newTagCategory: Omit<TagCategory, '_id' | 'createdBy'>) => void;
  parentCategory?: string;
  existingCategoryLabels: string[];
  createdTagCategory?: TagCategory;
  resetCreateTagCategory: () => void;
}

export interface TagFormData {
  label: string;
  colour: [string, TagColour];
  weight: number;
  category: { value: string; label: string };
  subcategory: { value: string; label: string } | null;
}

export interface TagFormProps {
  tagCategories: TagCategory[];
  existingTag?: Tag;
  isCreating: boolean;
  fieldsDisabled?: boolean;
  isModal: boolean;
}

export interface AddTagModalProps extends ModalProps {
  tagCategories: TagCategory[];
  handleCreateTag: (newTag: CreateTagRequest) => void;
}

export interface TagColour {
  borderColor: string;
  backgroundColor: string;
  fontColor: string;
  darkModeFontColor: string;
}

export type TagColourKey =
  | 'magenta'
  | 'red'
  | 'volcano'
  | 'orange'
  | 'gold'
  | 'lime'
  | 'green'
  | 'cyan'
  | 'blue'
  | 'darkblue'
  | 'purple'
  | 'violet'
  | 'lightgrey'
  | 'grey';

export const TagColours: Record<TagColourKey, TagColour> = {
  magenta: {
    borderColor: '#ffadd2',
    backgroundColor: '#fff0f6',
    fontColor: '#c41d7f',
    darkModeFontColor: '#c41d7f',
  },
  red: {
    borderColor: '#ffa39e',
    backgroundColor: '#fff1f0',
    fontColor: '#cf1322',
    darkModeFontColor: '#cf1322',
  },
  volcano: {
    borderColor: '#ffbb96',
    backgroundColor: '#fff2e8',
    fontColor: '#d4380d',
    darkModeFontColor: '#d4380d',
  },
  orange: {
    borderColor: '#ffd591',
    backgroundColor: '#fff7e6',
    fontColor: '#d46b08',
    darkModeFontColor: '#d46b08',
  },
  gold: {
    fontColor: '#d48806',
    darkModeFontColor: '#d48806',
    backgroundColor: '#fffbe6',
    borderColor: '#ffe58f',
  },
  lime: {
    fontColor: '#7cb305',
    darkModeFontColor: '#7cb305',
    backgroundColor: '#fcffe6',
    borderColor: '#eaff8f',
  },
  green: {
    fontColor: '#389e0d',
    darkModeFontColor: '#389e0d',
    backgroundColor: '#f6ffed',
    borderColor: '#b7eb8f',
  },
  cyan: {
    fontColor: '#08979c',
    darkModeFontColor: '#08979c',
    backgroundColor: '#e6fffb',
    borderColor: '#87e8de',
  },
  blue: {
    fontColor: '#0958d9',
    darkModeFontColor: '#4d90ff',
    backgroundColor: '#e6f4ff',
    borderColor: '#91caff',
  },
  darkblue: {
    fontColor: '#1d39c4',
    darkModeFontColor: '#126cff',
    backgroundColor: '#f0f5ff',
    borderColor: '#adc6ff',
  },
  purple: {
    fontColor: '#531dab',
    darkModeFontColor: '#853bff',
    backgroundColor: '#f9f0ff',
    borderColor: '#d3adf7',
  },
  violet: {
    fontColor: '#7F00FF',
    darkModeFontColor: '#7F00FF',
    backgroundColor: '#F4ECF7',
    borderColor: '#D2B4DE',
  },
  lightgrey: {
    fontColor: '#566573',
    darkModeFontColor: '#a3b6c7',
    backgroundColor: '#F7F9F9',
    borderColor: '#E5E7E9',
  },
  grey: {
    fontColor: '#273746',
    darkModeFontColor: '#FFFFFF',
    backgroundColor: '#ECF0F1',
    borderColor: '#BDC3C7',
  },
};
export interface RoomWeightedTag {
  id: string;
  weight: number;
}
